import { objectKeys } from "app/helpers/objects";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { type SignalType, signalTypeMap } from "../types/signal";
import type { SignalsGroupedByType, SignalsGroupedByTypeKey } from "../types/signal-collection";
import type { SignalTypeKey } from "../types/signal-key";

const SIGNAL_TYPE_VALUES = Object.values(signalTypeMap);

export const toSignalTypeKey = <T extends SignalType>(signalType: T): SignalTypeKey => `signals${signalType}`;

export const isSignalTypeKey = (key: PropertyKey): key is SignalTypeKey =>
    SIGNAL_TYPE_VALUES.some((signalType) => key === toSignalTypeKey(signalType));

const SIGNAL_TYPE_BY_KEY = SIGNAL_TYPE_VALUES.reduce<Record<SignalTypeKey, SignalType>>(
    (acc, signalType) => ({ ...acc, [toSignalTypeKey(signalType)]: signalType }),
    {} as Record<SignalTypeKey, SignalType>,
);

export const signalTypeForKey = (signalTypeKey: SignalTypeKey): SignalType => SIGNAL_TYPE_BY_KEY[signalTypeKey];

type SignalKeyPartition<T> = {
    readonly rest: Partial<Omit<T, keyof SignalsGroupedByTypeKey>>;
    readonly signals: Partial<SignalsGroupedByType>;
};

/**
 * Partitions a record into signal-keyed entries (rekeyed to canonical SignalType)
 * and the remaining non-signal entries. Single-pass.
 */
export const partitionBySignalKeys = <T extends Partial<SignalsGroupedByTypeKey>>(source: T): SignalKeyPartition<T> =>
    objectKeys(source).reduce<SignalKeyPartition<T>>(
        (acc, key) => {
            if (isSignalTypeKey(key)) {
                const value = source[key];

                if (is.undefined(value)) {
                    return acc;
                }

                acc.signals[signalTypeForKey(key)] = value;

                return acc;
            }

            return { ...acc, rest: { ...acc.rest, [key]: source[key] } };
        },
        { rest: {}, signals: {} } as SignalKeyPartition<T>,
    );
